'use client'

import { motion } from 'framer-motion'
import { cn } from '@/lib/utils'
import { Icons } from '@/components/ui/icons'
import { isManager } from '@/lib/roleUtils'

interface Capability {
  icon: React.ElementType
  label: string
  query: string
  /** If true, only admin/mrm/qam users see this suggestion (matches ADMIN_ONLY_TOOLS gate) */
  managementOnly?: boolean
}

const CAPABILITIES: Capability[] = [
  { icon: Icons.alertTriangle, label: 'Show overdue claims', query: 'Show me overdue claims' },
  { icon: Icons.trendingUp, label: 'High-dollar claims above $10K', query: 'Find claims with billed amount above $10,000' },
  { icon: Icons.barChart, label: 'Generate a report', query: 'I want to generate a report' },
  { icon: Icons.activity, label: 'SLA health summary', query: 'What is the current SLA health status?', managementOnly: true },
  { icon: Icons.users, label: 'Reviewer workload', query: 'Show me reviewer workload summary', managementOnly: true },
  { icon: Icons.download, label: 'Export UTL', query: 'Export the Universe Tracking Log', managementOnly: true },
  { icon: Icons.fileText, label: 'Show open RFIs', query: 'Show me open RFIs' },
  { icon: Icons.info, label: 'Explain this page', query: 'Explain this page to me' },
]

interface CapabilityBubblesProps {
  onSelect: (query: string) => void
  userRoles?: string[]
}

export function CapabilityBubbles({ onSelect, userRoles = [] }: CapabilityBubblesProps) {
  // Use isManager so mrm and qam also see management tool suggestions (RBAC Matrix §11)
  const canManage = isManager(userRoles)
  const visibleCapabilities = CAPABILITIES.filter(cap => !cap.managementOnly || canManage)

  return (
    <div className="flex flex-wrap justify-center gap-2 p-2">
      {visibleCapabilities.map((cap, i) => {
        const Icon = cap.icon
        return (
          <motion.button
            key={i}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.06, duration: 0.25, ease: 'easeOut' }}
            onClick={() => onSelect(cap.query)}
            className={cn(
              'flex items-center gap-2 px-3 py-2 rounded-full',
              'bg-white border border-brand-cornflower/20',
              'text-sm text-brand-navy',
              'shadow-sm',
              'transition-all duration-200',
              'hover:bg-brand-cornflower/10 hover:border-brand-cornflower/40 hover:shadow-md',
              'focus:outline-none focus:ring-2 focus:ring-brand-cornflower/50'
            )}
          >
            <Icon className="h-4 w-4 text-brand-cornflower" strokeWidth={1.5} />
            <span className="text-xs sm:text-sm font-medium">{cap.label}</span>
          </motion.button>
        )
      })}
    </div>
  )
}

